import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGift, FaHeart, FaRose, FaGem, FaCrown, FaStar, FaMoon, FaSun } from 'react-icons/fa';

// Gift types must match backend validation: 'small', 'medium', 'large', 'premium'
// Prices must match DiamantPricingService::COSTS in backend
const virtualGifts = [
  { id: 'small', name: 'Rose', icon: '🌹', price: 20, color: '#FF1744', animation: 'float' },
  { id: 'medium', name: 'Cœur', icon: '❤️', price: 50, color: '#E91E63', animation: 'pulse' },
  { id: 'large', name: 'Bouquet', icon: '💐', price: 100, color: '#FF69B4', animation: 'bounce' },
  { id: 'premium', name: 'Diamant', icon: '💎', price: 200, color: '#00BCD4', animation: 'sparkle' },
];



const VirtualGift = ({ onSend, onClose, isVisible, userBalance = 0 }) => {

  const [selectedGift, setSelectedGift] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [sentGift, setSentGift] = useState(null);

  if (!isVisible) return null;

  const handleGiftClick = (gift) => {
    setSelectedGift(gift);
  };

  const handleConfirmSend = () => {
    if (selectedGift && userBalance >= selectedGift.price) {
      setSentGift(selectedGift);
      setShowAnimation(true);
      onSend(selectedGift);
      
      setTimeout(() => {
        setShowAnimation(false);
        setSentGift(null);
        setSelectedGift(null);
        onClose?.();
      }, 3000);
    }
  };

  const handleClose = () => {
    setSelectedGift(null);
    onClose?.();
  };


  return (
    <motion.div
      className="virtual-gift-panel"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
    >
      <AnimatePresence mode="wait">
        {showAnimation && sentGift ? (
          <motion.div
            key="animation"
            className="gift-animation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="gift-emoji"
              animate={{
                y: [0, -100, -50, -150, 0],
                scale: [1, 1.5, 1.2, 1.8, 1],
                rotate: [0, -15, 15, -10, 0]
              }}
              transition={{ duration: 2, ease: "easeInOut" }}
            >
              {sentGift.icon}
            </motion.div>
            <motion.div
              className="gift-message"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3>{sentGift.name} envoyé !</h3>
              <p>Un geste romantique envoyé avec amour 💕</p>
            </motion.div>
            <div className="sparkles">
              {Array.from({ length: 20 }).map((_, i) => (
                <motion.span
                  key={i}
                  className="sparkle"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    x: Math.random() * 200 - 100,
                    y: Math.random() * 200 - 100
                  }}
                  transition={{ 
                    duration: 1.5,
                    delay: Math.random() * 0.5
                  }}
                >
                  ✨
                </motion.span>
              ))}
            </div>
          </motion.div>
        ) : selectedGift ? (
          <motion.div
            key="confirm"
            className="gift-confirm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="selected-gift-display">
              <motion.span 
                className="selected-gift-emoji"
                animate={{ 
                  y: [0, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                {selectedGift.icon}
              </motion.span>
              <h3>{selectedGift.name}</h3>
              <p className="gift-price">
                <FaGem /> {selectedGift.price} diamants
              </p>
            </div>
            <div className="confirm-actions">
              <button 
                className="btn-cancel"
                onClick={handleClose}
              >
                Annuler
              </button>

              <button 
                className={`btn-send ${userBalance < selectedGift.price ? 'disabled' : ''}`}
                onClick={handleConfirmSend}
                disabled={userBalance < selectedGift.price}
              >
                {userBalance < selectedGift.price ? 'Solde insuffisant' : 'Envoyer 💝'}
              </button>
            </div>
            <p className="balance-info">
              Votre solde: <strong>{userBalance} diamants</strong>
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="grid"
            className="gift-grid-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="gift-header">
              <FaGift className="gift-icon" />
              <span>Offrir un cadeau virtuel</span>
              <button 
                className="close-btn"
                onClick={handleClose}
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            <div className="gift-grid">
              {virtualGifts.map((gift, index) => (
                <motion.button
                  key={gift.id}
                  className="gift-item"
                  style={{ '--gift-color': gift.color }}
                  onClick={() => handleGiftClick(gift)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ 
                    scale: 1.1,
                    boxShadow: `0 8px 25px ${gift.color}50`
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="gift-emoji">{gift.icon}</span>
                  <span className="gift-name">{gift.name}</span>
                  <span className="gift-cost">
                    <FaGem /> {gift.price}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default VirtualGift;
